import React from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import voca from 'voca';
import CustomReactPaginate from '../../../common/custom/CustomReactPaginate';
import { PRODUCTION, PREVIEW } from '../../../common/utils/environments';

/**
 * Calculates human-readable time remaining until expiration with appropriate color coding:
 * - Never: Green / Neutral (No expiration)
 * - Expired: Gray / Red (Already expired)
 * - Short (< 24 hours): Red
 * - Medium (1 to 29 days): Yellow / Amber
 * - Long (>= 30 days / 1 month or more): Green
 */
export const calculateExpiryInfo = (expiresAt) => {
  if (!expiresAt) {
    return {
      text: 'Never',
      subText: 'No expiration',
      colorClass: 'expiry-never',
      status: 'never',
    };
  }

  const expDate = new Date(expiresAt);
  const now = new Date();
  const diffMs = expDate.getTime() - now.getTime();
  const dateStr = expDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  if (diffMs <= 0) {
    return {
      text: 'Expired',
      subText: dateStr,
      colorClass: 'expiry-expired',
      status: 'expired',
    };
  }

  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  // Short period (< 24 hours or minutes) -> Red
  if (diffHours < 1) {
    const mins = Math.max(1, diffMin);
    return {
      text: `in ${mins} min${mins === 1 ? '' : 's'}`,
      subText: dateStr,
      colorClass: 'expiry-short',
      status: 'short',
    };
  }

  if (diffHours < 24) {
    return {
      text: `in ${diffHours} hour${diffHours === 1 ? '' : 's'}`,
      subText: dateStr,
      colorClass: 'expiry-short',
      status: 'short',
    };
  }

  // Medium period (1 day up to 29 days) -> Yellow
  if (diffDays < 30) {
    if (diffDays === 1) {
      return {
        text: 'in 1 day',
        subText: dateStr,
        colorClass: 'expiry-medium',
        status: 'medium',
      };
    }
    if (diffDays === 7) {
      return {
        text: 'in 7 days (1 week)',
        subText: dateStr,
        colorClass: 'expiry-medium',
        status: 'medium',
      };
    }
    if (diffDays % 7 === 0) {
      const weeks = diffDays / 7;
      return {
        text: `in ${weeks} week${weeks > 1 ? 's' : ''}`,
        subText: dateStr,
        colorClass: 'expiry-medium',
        status: 'medium',
      };
    }
    return {
      text: `in ${diffDays} days`,
      subText: dateStr,
      colorClass: 'expiry-medium',
      status: 'medium',
    };
  }

  // Long period (>= 30 days / 1 month or more) -> Green
  if (diffDays < 365) {
    const months = Math.round(diffDays / 30);
    return {
      text: `in ${months} month${months > 1 ? '' : 's'}`,
      subText: dateStr,
      colorClass: 'expiry-long',
      status: 'long',
    };
  }

  const years = Math.round((diffDays / 365) * 10) / 10;
  const formattedYears = years % 1 === 0 ? years : years.toFixed(1);
  return {
    text: `in ${formattedYears} year${formattedYears === 1 || formattedYears === '1.0' ? '' : 's'}`,
    subText: dateStr,
    colorClass: 'expiry-long',
    status: 'long',
  };
};

export const renderExpiryBadge = (expiresAt) => {
  const expiry = calculateExpiryInfo(expiresAt);
  return (
    <div className={`expiry-indicator ${expiry.colorClass}`}>
      <div className="expiry-main">
        <span className="expiry-dot" />
        <span className="expiry-text">{expiry.text}</span>
      </div>
      <div className="expiry-sub">({expiry.subText})</div>
    </div>
  );
};

export default function DevApiKeyTable({
  devApiKeys = [],
  isFetching = false,
  searchTerm = '',
  statusFilter = 'ALL',
  selectedEnv = 'ALL',
  handleClearFilters,
  openCreateModal,
  copiedKeyId = '',
  handleCopyPrefix,
  handleToggleStatus,
  handleRevoke,
  handleDelete,
  deleteFlag = true,
  totalCount = 0,
  currentPage = 1,
  limit = 10,
  totalPages = 1,
  setCurrentPage,
  isReadOnly,
}) {
  if (isFetching && devApiKeys.length === 0) {
    return (
      <div className="keys-table-card">
        <div className="p-5 text-center text-muted">
          <div className="spinner-border spinner-border-sm text-primary mb-2" role="status" />
          <p className="small mb-0">Loading developer API keys...</p>
        </div>
      </div>
    );
  }

  if (devApiKeys.length === 0) {
    return (
      <div className="keys-table-card">
        <div className="p-5 text-center text-muted">
          <i className="webconnect webconnect-key display-4 d-block mb-3 text-secondary" />
          <h5>
            {searchTerm || statusFilter !== 'ALL'
              ? 'No matching API keys found'
              : selectedEnv !== 'ALL'
              ? `No API keys for ${selectedEnv} environment`
              : 'No Developer API Keys yet'}
          </h5>
          <p className="small mb-3">
            {searchTerm || statusFilter !== 'ALL'
              ? 'Try adjusting your search query or status filter.'
              : 'Generate your first API key to authenticate external requests.'}
          </p>
          {searchTerm || statusFilter !== 'ALL' ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClearFilters}
            >
              Clear Filters
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-theme-submit btn-sm"
              onClick={isReadOnly ? null : openCreateModal}
              disabled={isReadOnly}
              title={isReadOnly && 'Read-Only Mode'}
            >
              <i className="webconnect webconnect-plus mr-1" />
              Generate Key
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="keys-table-card">
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Key Name / Prefix</th>
              <th style={{ width: '12%' }}>Environment</th>
              <th style={{ width: '12%' }}>Status</th>
              <th style={{ width: '18%' }}>Expiration</th>
              <th style={{ width: '14%' }}>Limits & IPs</th>
              <th style={{ width: '16%', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devApiKeys.map((key) => {
              const keyId = key._id || key.uuid;
              const isActive = key.status === 'active';
              const isInactive = key.status === 'inactive';

              const envClass =
                key.environment === PRODUCTION
                  ? 'env-production'
                  : key.environment === PREVIEW
                  ? 'env-preview'
                  : 'env-other';

              return (
                <tr key={keyId}>
                  {/* Name & Token Prefix */}
                  <td>
                    <div className="key-name-text">{key.label}</div>
                    <div className="key-token-tag">
                      <span>{key.keyPrefix}••••••••••••</span>
                      <CopyToClipboard
                        text={key.keyPrefix}
                        onCopy={() => handleCopyPrefix && handleCopyPrefix(keyId)}
                      >
                        <span
                          className="copy-icon-btn"
                          title={copiedKeyId === keyId ? 'Copied Prefix!' : 'Copy prefix'}
                        >
                          {copiedKeyId === keyId ? (
                            <i className="webconnect webconnect-check text-success" />
                          ) : (
                            <i className="webconnect webconnect-files" />
                          )}
                        </span>
                      </CopyToClipboard>
                    </div>
                  </td>

                  {/* Environment */}
                  <td>
                    <span className={`env-badge-pill ${envClass}`}>
                      {voca.titleCase(key.environment)}
                    </span>
                  </td>

                  {/* Status */}
                  <td>
                    <span className={`status-badge-pill status-${key.status}`}>
                      <span className="status-dot" />
                      {voca.titleCase(key.status)}
                    </span>
                  </td>

                  {/* Expiration */}
                  <td>
                    {renderExpiryBadge(key.expiresAt)}
                    {key.lastUsedAt && (
                      <div className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>
                        Last used: {new Date(key.lastUsedAt).toLocaleDateString()}
                      </div>
                    )}
                  </td>

                  {/* Limits & IPs */}
                  <td>
                    <div className="small font-weight-bold text-dark">
                      {key.rateLimit || 120} req/min
                    </div>
                    <div className="text-muted" style={{ fontSize: '11.5px' }}>
                      {key.allowedIps && key.allowedIps.length > 0
                        ? `${key.allowedIps.length} IP(s) allowed`
                        : 'All IPs'}
                    </div>
                  </td>

                  {/* Single Row Actions */}
                  <td>
                    <div className="table-actions-row">
                      {isActive && (
                        <button
                          type="button"
                          className="btn-table-action btn-act-deactivate"
                          title={isReadOnly ? 'Read-Only Mode' : 'Deactivate Key'}
                          onClick={() =>
                            !isReadOnly && handleToggleStatus && handleToggleStatus(key)
                          }
                          disabled={isReadOnly}
                        >
                          <i className="webconnect webconnect-pause" />
                          Deactivate
                        </button>
                      )}

                      {isInactive && (
                        <button
                          type="button"
                          className="btn-table-action btn-act-reactivate"
                          title={isReadOnly ? 'Read-Only Mode' : 'Reactivate Key'}
                          onClick={() =>
                            !isReadOnly && handleToggleStatus && handleToggleStatus(key)
                          }
                          disabled={isReadOnly}
                        >
                          <i className="webconnect webconnect-play" />
                          Reactivate
                        </button>
                      )}

                      {(isActive || isInactive) && (
                        <button
                          type="button"
                          className="btn-table-action btn-act-revoke"
                          title={isReadOnly ? 'Read-Only Mode' : 'Revoke Key Permanently'}
                          onClick={() => !isReadOnly && handleRevoke && handleRevoke(key)}
                          disabled={isReadOnly}
                        >
                          <i className="webconnect webconnect-slash-circle" />
                          Revoke
                        </button>
                      )}

                      {deleteFlag && (
                        <button
                          type="button"
                          className="btn-table-action btn-act-delete"
                          title={
                            isReadOnly
                              ? 'Read-Only Mode'
                              : isActive
                              ? 'Deactivate or revoke key before deleting'
                              : 'Delete Key'
                          }
                          disabled={isActive || isReadOnly}
                          onClick={() => !isReadOnly && handleDelete && handleDelete(key)}
                        >
                          <i className="wb webconnect-trash-regular" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination & Count Footer */}
      <div className="keys-table-footer">
        <div className="keys-footer-count">
          Showing {totalCount === 0 ? 0 : (currentPage - 1) * limit + 1} to{' '}
          {Math.min(currentPage * limit, totalCount)} of {totalCount} keys
        </div>
        {totalPages > 1 && setCurrentPage && (
          <div className="dev-keys-pagination-wrapper">
            <CustomReactPaginate
              totalPages={totalPages}
              pageRangeDisplayed={5}
              marginPagesDisplayed={1}
              page={currentPage - 1}
              onPageChange={({ selected }) => setCurrentPage(selected + 1)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
